/* eslint-disable react/prop-types */
import { Box, Checkbox } from "@mui/material";
import { Form, Formik } from "formik";
import { compose, pickBy } from "lodash/fp";
import React, { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import Button from "../../../components/common/Button";
import { CommonStyles } from "../../../components/common/Button/commonButtonStyle";
import Toast from "../../../components/common/Toast";
import MemberInfo from "../../../components/MemberInfo/MemberInfo";
import PledgeList from "../../../components/PledgeList/PledgeList";
import { okatakiGohonzonQuestions } from "../../../contants/gohonzon";
import useGohonzon from "../../../hooks/useGohonzon";
import {
  selectGohonzonType,
  selectMemberForGohonzonApply,
} from "../../../redux/selectors/gohonzon";
import {
  selectAllSIUsers,
  selectOSIdByUserRole,
  selectUserProfile,
} from "../../../redux/selectors/user";
import Gohonzon from "../../../services/api/gohonzon";
import Member from "../../../services/api/member";
import { ROUTES } from "../../../utils/constants";
import { debounce, isEmptyObj } from "../../../utils/helper";
import GohonzonOkatakiForm from "./GohonzonOkatakiForm/GohonzonOkatakiForm";
import HomeVisitedRecommended from "./GohonzonOkatakiForm/HomeVisitedRecommended";
import OkatakiFormQuestion from "./GohonzonOkatakiForm/OkatakiFormByMember";
import GohonzonOmamoriForm from "./GohonzonOmamoriForm/GohonzonOmamoriForm";
import GohonzonReturnForm from "./GohonzonReturnForm/GohonzonReturnForm";
import {
  formValidate,
  getErrorInfo,
  getFormattedOptions,
  getSuccessInfo,
  getTitle,
  okatakiInterviewByRegionValidation,
  prepareReturnOrReplaceBody,
  validateReturnOrReplaceValues,
} from "./helper";
import MemberInterviewedForm from "./MemberInterviewedForm/MemberInterviewedForm";
import MemberRelationship from "./MemberRelationship/MemberRelationship";
import { ToastAlertContext } from "../../../config/providers/ToastAlert";
import GohonzonInterview from "./Modals";
import TrackingModal from "./Modals/TrackingModal";

function Declaration({ handleCheckBox }) {
  return (
    <>
      <Checkbox size="small" onChange={handleCheckBox} />
      <span
        style={{
          fontSize: "12px",
          color: "#484848",
          fontFeatureSettings: "'clig' off, 'liga' off",
          fontStyle: "normal",
          fontWeight: "600",
          lineWeight: "20px",
        }}
      >
        {" "}
        I hereby declare that the above information filled by me are correct as
        per the applicant{" "}
      </span>
      <br />
    </>
  );
}

function GohonzonSubmission() {
  const memberInfo = useSelector(selectMemberForGohonzonApply);
  const applyingGohonzonType = useSelector(selectGohonzonType);
  const userOS = useSelector(selectOSIdByUserRole);
  const userProfile = useSelector(selectUserProfile);
  const [showToast, setShowToast] = useState(null);
  const [checked, setChecked] = useState(false);
  const [memberRelationship, setMemberRelationship] = useState(null);
  const [options, setOptions] = useState([]);
  const [regionIds, setRegionIds] = useState([]);
  const [chapterIds, setChapterIds] = useState([]);
  const [chapterOptions, setChapterOptions] = useState([]);
  const [openInterviewModal, setOpenInterviewModal] = useState(false);
  const [openTrackingModal, setOpenTrackingModal] = useState(false);

  const { type } = useParams();
  const isTracking = type === "tracking";

  const onOpenTrackingModal = () => {
    setOpenTrackingModal(true);
  };

  const toggleTrackingModal = () => {
    setOpenTrackingModal((prev) => !prev);
  };

  const toggleInterviewMoodal = () => {
    setOpenInterviewModal((prev) => !prev);
  };

  const toast = useContext(ToastAlertContext);

  const gohonzonType = memberInfo.gohonzonType || applyingGohonzonType;
  const {
    readOnly,
    isReturn,
    isPreview,
    showPlegde,
    isInterviewZoneSi,
    isInterviewRegionSi,
    showOkatakiForm,
    showOmamoriForm,
    getInitialFormData,
    showMemberRelationship,
    showMemberInterviewedForm,
    showHomeVisitedRecommended,
    showOkatakiFormByMember,
    showOkatakiFormByChapterSi,
    showOkatakiFormByRegionSi,
  } = useGohonzon(gohonzonType);

  const {
    isDistrictSi,
    isChapterSi,
    isRegionSi,
    isZoneSi,
    isHelpDesk,
    isHelpDeskAdmin,
  } = useSelector(selectAllSIUsers);
  const navigate = useNavigate();
  const memberDetails =
    (isDistrictSi && !isTracking) || isReturn
      ? memberInfo
      : memberInfo?.memberDetails;

  const goBack = () => {
    if (isDistrictSi) navigate(ROUTES.APPLY_GOHONZON.path);
    else if (isChapterSi) navigate(ROUTES.GOHONZON_APPLICATIONS.path);
    else if (isInterviewRegionSi)
      navigate(`/${ROUTES.GOHONZON_INTERVIEW.path}`);
    else if (isInterviewZoneSi) navigate(`/${ROUTES.GOHONZON_APPROVAL.path}`);
    else if (isReturn && isRegionSi) navigate(-1);
    else if (isReturn && isZoneSi)
      navigate(`/${ROUTES.GOHONZON_RETURN_VERIFICATION.path}`);
  };

  const getRecommendSi = () => {
    let obj = {};
    if (isChapterSi) {
      obj = { recommendedByChapterChief: "APPROVED" };
    } else if (isRegionSi) {
      obj = { recommendedByRegionChief: "APPROVED" };
    } else if (isZoneSi) {
      obj = { recommendedByZoneChief: "APPROVED" };
    }
    return { memberId: memberInfo.memberId, ...obj };
  };

  const getAnswer = (question, answer) => {
    if (question.answerType === "boolean") return answer === "YES";
    if (question.answerType === "integer") return Number(answer);
    return answer;
  };

  const prepareBody = (formValues = {}) => {
    const getAllBooleanField = (trueValues) => {
      return okatakiGohonzonQuestions.reduce(
        (acc, curr) => [
          ...acc,
          {
            questionId: Number(curr.name.split("-")[1]),
            answer: getAnswer(curr, trueValues[curr.key]),
          },
        ],
        []
      );
    };

    const getOkatakiInputsPayload = (result) => {
      if (gohonzonType === "OKATAKI_GOHONZON") {
        return {
          chapterAnswers: result,
        };
      }
      if (gohonzonType === "OMAMORI_GOHONZON") {
        return formValues;
      }
      return {};
    };

    const getOkatakiInputsPayloadRegion = (result) => {
      if (gohonzonType === "OKATAKI_GOHONZON") {
        return {
          regionAnswers: result,
        };
      }
      return {};
    };

    return isInterviewRegionSi
      ? compose(
          getOkatakiInputsPayloadRegion,
          getAllBooleanField,
          pickBy(Boolean)
        )(formValues)
      : compose(
          getOkatakiInputsPayload,
          getAllBooleanField,
          pickBy(Boolean)
        )(formValues);
  };

  const getAPI = () => {
    return isDistrictSi
      ? "initiateGohonzonApplication"
      : "gohonzonVerification";
  };

  const getParams = () => {
    return isDistrictSi ? memberDetails.memberId : memberInfo?.id;
  };

  const getSubmitted = () => {
    if (isChapterSi) {
      return { homeVisitedBy: chapterIds };
    }
    return {};
  };
  const fetchRegionSiList = async () => {
    try {
      const result = await Gohonzon.getRegionSiList({
        zoneId: userProfile.zoneId,
        areaId: userProfile.areaId,
        regionId: userProfile.regionId,
      });
      setOptions(getFormattedOptions(result.data));
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const fetchChapterChiefList = async () => {
    try {
      const result = await Gohonzon.getChapterChiefList({
        zoneId: userProfile.zoneId,
        areaId: userProfile.areaId,
        regionId: userProfile.regionId,
        chapterId: userProfile.chapterId,
      });
      setChapterOptions(getFormattedOptions(result.data));
    } catch (err) {
      console.log(err);
    }
  };

  const handleCheckBox = (e) => {
    setChecked(e.target.checked);
  };

  const onAddMember = () => {
    navigate(`/${ROUTES.EDIT_FAMILY_MEMBER.path}/${memberInfo.familyId}`);
  };

  const onCreateFamily = () => {
    navigate(`/${ROUTES.CREATE_NEW_FAMILY.path}`);
  };

  const fetchMemberRelationship = async (id) => {
    try {
      const { data } = await Member.getRelationshipsByMemberId(id);
      setMemberRelationship(data.content);
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const onFormSubmit = async (values) => {
    try {
      console.log("values: ", values);
      const body = prepareBody(values);
      const error = formValidate(chapterIds, values, gohonzonType, {
        isChapterSi,
        isDistrictSi,
      });
      if (error) throw new Error(error);
      if (!checked) throw new Error("Please click on agree button"); // todo: remove

      await Gohonzon[getAPI()](
        {
          ...body,
          ...getSubmitted(),
          formType: "APPLICATION",
          gohonzonType,
          ...getRecommendSi(),
          ...userOS,
        },
        getParams()
      );
      setShowToast(getSuccessInfo());
      setTimeout(() => {
        goBack();
      }, 2000);
    } catch (err) {
      setShowToast(getErrorInfo(err.message));
      Sentry.captureException(err);
    }
  };

  const handleInterviewSubmit = async (values) => {
    try {
      const anyValueIsNo = Object.values(values).some(
        (value) => value === "NO"
      );

      const body = prepareBody(values);
      const error = okatakiInterviewByRegionValidation(
        regionIds,
        values,
        gohonzonType,
        {
          isInterviewRegionSi,
        }
      );
      if (error) throw new Error(error);
      if (anyValueIsNo) {
        // Open the GohonzonInterviewModal
        setOpenInterviewModal(true);
      } else {
        await Gohonzon.submitInterViewByRegionSi(
          {
            ...body,
            interviewedBy: regionIds,
            formType: "APPLICATION",
            memberId: memberInfo.memberId,
            gohonzonType: memberInfo.gohonzonType,
          },
          memberInfo.id
        );
        setShowToast(getSuccessInfo());
        setTimeout(() => {
          goBack();
        }, 2000);
      }
    } catch (err) {
      setShowToast(getErrorInfo(err.message));
      Sentry.captureException(err);
    }
  };

  const handleInterviewSubmitByZone = async () => {
    try {
      await Gohonzon.submitInterViewByZoneSi(
        {
          formType: "APPLICATION",
          memberId: memberInfo.memberId,
          gohonzonType: memberInfo.gohonzonType,
        },
        memberInfo.memberId,
        memberInfo.id
      );
      setShowToast(getSuccessInfo());
      setTimeout(() => {
        goBack();
      }, 2000);
    } catch (err) {
      setShowToast(getErrorInfo(err.message));
      Sentry.captureException(err);
    }
  };

  const handleReturnSubmit = async (values) => {
    try {
      const body = prepareReturnOrReplaceBody(values);
      const error = validateReturnOrReplaceValues(body, values.replaceCheck);
      if (error) throw new Error(error);
      await Gohonzon.GohonzonReturnRequest(
        {
          formType: values.replaceCheck ? "REPLACEMENT" : "RETURN",
          memberId: memberInfo.memberId,
          gohonzonType: memberInfo.gohonzonMemberships?.[0]?.type,
          ...body,
        },
        memberInfo.memberId
      );
      setShowToast(getSuccessInfo());
      setTimeout(() => {
        goBack();
      }, 2000);
    } catch (err) {
      setShowToast(getErrorInfo(err.message));
      Sentry.captureException(err);
    }
  };

  const getHandleSubmit = () => {
    if (isInterviewRegionSi) {
      return handleInterviewSubmit;
    }
    if (isReturn && isRegionSi) {
      return handleReturnSubmit;
    }
    // if (isReturn && isZoneSi) {
    //   return returnVerificationSubmit;
    // }
    if (isInterviewZoneSi) {
      return handleInterviewSubmitByZone;
    }
    return onFormSubmit;
  };

  useEffect(() => {
    if (isInterviewRegionSi) {
      fetchRegionSiList();
    }
    if (isChapterSi) {
      fetchChapterChiefList();
    }
  }, [isInterviewRegionSi, isChapterSi]);

  useEffect(() => {
    if (!isEmptyObj(memberInfo)) {
      fetchMemberRelationship(memberInfo.memberId);
    }
  }, [memberInfo]);

  return (
    <>
      <Box
        style={{
          margin: "-2% -2.8%",
        }}
      >
        <Formik
          initialValues={getInitialFormData(memberInfo)}
          onSubmit={debounce(getHandleSubmit())}
        >
          {({ values, handleChange, handleSubmit, setFieldValue }) => {
            return (
              <>
                <Form>
                  <Box
                    style={{
                      borderRadius: "14px",
                      border: "1px solid #E2E2E2",
                      background: "#F9F9F9",
                      boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
                      padding: 0,
                    }}
                  >
                    <MemberInfo
                      heading={getTitle(gohonzonType, isReturn)}
                      title1="Personal Details"
                      title2="Organization Structure"
                      memberInfo={memberInfo}
                      memberDetails={memberDetails}
                      gohonzonType={gohonzonType}
                      onOpenTrackingModal={onOpenTrackingModal}
                    />
                    {(isDistrictSi || isRegionSi) && <Box mb={6} />}
                    {showOkatakiFormByMember() && (
                      <OkatakiFormQuestion
                        title="Other Details by Member"
                        values={values}
                        answers={memberInfo?.memberAnswers}
                        handleChange={handleChange}
                        readOnly={readOnly}
                        setFieldValue={setFieldValue}
                        member
                      />
                    )}
                    {showOkatakiFormByChapterSi() && (
                      <OkatakiFormQuestion
                        title="Other Details by Chapter"
                        name={memberInfo?.submittedBy}
                        values={values}
                        answers={memberInfo?.chapterAnswers}
                        handleChange={handleChange}
                        readOnly={readOnly}
                        setFieldValue={setFieldValue}
                      />
                    )}
                    {showOkatakiFormByRegionSi() && (
                      <OkatakiFormQuestion
                        title="Other Details by Region"
                        name={memberInfo?.regionApprovedBy}
                        values={values}
                        answers={memberInfo?.regionAnswers}
                        handleChange={handleChange}
                        readOnly={readOnly}
                        setFieldValue={setFieldValue}
                      />
                    )}
                    {showOkatakiForm() && (
                      <GohonzonOkatakiForm
                        values={values}
                        handleChange={handleChange}
                        readOnly={readOnly}
                        setFieldValue={setFieldValue}
                      />
                    )}
                    {/* <Divider /> */}
                    {showMemberRelationship() && (
                      <MemberRelationship
                        memberRelationship={memberRelationship}
                        // showAddLink={isDistrictSi}
                        showAddLink={!readOnly}
                        onAddMember={onAddMember}
                        onCreateFamily={onCreateFamily}
                      />
                    )}
                    {showOmamoriForm() && (
                      <GohonzonOmamoriForm
                        values={values}
                        handleChange={handleChange}
                        readOnly={readOnly}
                      />
                    )}
                    {isReturn && (
                      <GohonzonReturnForm
                        handleChange={handleChange}
                        values={values}
                        setFieldValue={setFieldValue}
                        readOnly={readOnly}
                      />
                    )}

                    {!isReturn &&
                      isHelpDesk &&
                      isHelpDeskAdmin &&
                      gohonzonType === "OKATAKI_GOHONZON"}

                    {showMemberInterviewedForm() && (
                      <MemberInterviewedForm
                        regionIds={regionIds}
                        interviewedBy={memberInfo?.interviewedByMembers}
                        options={options}
                        setRegionIds={setRegionIds}
                        values={values}
                        setFieldValue={setFieldValue}
                        handleChange={handleChange}
                        readOnly={readOnly}
                        memberInfo={memberInfo}
                      />
                    )}
                    {showHomeVisitedRecommended() && (
                      <>
                        {!isReturn && <Box style={{ marginBottom: "20px" }} />}
                        <HomeVisitedRecommended
                          chapterIds={chapterIds}
                          setChapterIds={setChapterIds}
                          values={values}
                          setFieldValue={setFieldValue}
                          options={chapterOptions}
                          submittedBy={memberInfo?.homeVisitedByName}
                          memberInfo={memberInfo}
                          readOnly={readOnly}
                        />
                      </>
                    )}
                    {showPlegde() && (
                      <Box mt={7}>
                        <PledgeList />
                      </Box>
                    )}
                    {!isPreview && !isRegionSi}
                    <Box my={4} style={{ paddingLeft: "20px" }}>
                      {!isPreview && !isRegionSi && (
                        <Declaration handleCheckBox={handleCheckBox} />
                      )}
                      {!isPreview && (
                        <div
                          style={{
                            display: "flex",
                            gap: 5,
                            justifyContent: "end",
                            marginRight: "20px",
                          }}
                        >
                          <Button
                            onClick={goBack}
                            variant="outlined"
                            style={{
                              ...CommonStyles.buttonStyle,
                              marginRight: "20px",
                            }}
                            label="Cancel"
                          />
                          <Button
                            variant="contained"
                            onClick={handleSubmit}
                            style={CommonStyles.buttonStyle}
                            type="submit"
                            label="Submit"
                          />
                        </div>
                      )}
                    </Box>
                    <GohonzonInterview
                      open={openInterviewModal}
                      onClose={toggleInterviewMoodal}
                      values={values}
                      toast={toast}
                      goBack={goBack}
                      gohonzonType={gohonzonType}
                      isInterviewRegionSi={isInterviewRegionSi}
                      memberInfo={memberInfo}
                      regionIds={regionIds}
                      prepareBody={prepareBody}
                    />
                  </Box>
                </Form>
                <TrackingModal
                  open={openTrackingModal}
                  onClose={toggleTrackingModal}
                  memberData={memberInfo}
                />
              </>
            );
          }}
        </Formik>
      </Box>
      <Toast
        open={!!showToast}
        severity={showToast?.severity || ""}
        message={showToast?.message || ""}
        handleClose={() => {
          setShowToast(null);
        }}
      />
    </>
  );
}

export default GohonzonSubmission;
